<template>
  <div class="analysis-report">
    <a-page-header title="分析报告" sub-title="测试执行数据分析">
      <template #extra>
        <a-space>
          <a-range-picker v-model:value="dateRange" @change="fetchData" />
          <a-button @click="exportReport">
            <template #icon><DownloadOutlined /></template>
            导出报告
          </a-button>
          <a-button type="primary" @click="fetchData">
            <template #icon><ReloadOutlined /></template>
            刷新
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <!-- 筛选器 -->
      <a-card style="margin-top: 16px">
        <a-form layout="inline">
          <a-form-item label="测试计划">
            <a-select 
              v-model:value="filters.test_plan_id" 
              style="width: 200px" 
              allow-clear
              placeholder="全部"
              @change="fetchData"
            >
              <a-select-option v-for="plan in testPlans" :key="plan.id" :value="plan.id">
                {{ plan.plan_name }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="测试用例">
            <a-select 
              v-model:value="filters.test_case_id" 
              style="width: 200px" 
              allow-clear
              placeholder="全部"
              @change="fetchData"
            >
              <a-select-option v-for="tc in testCases" :key="tc.id" :value="tc.id">
                {{ tc.case_name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </a-card>

      <!-- 总体概览 -->
      <a-card title="总体概览" style="margin-top: 16px" v-if="overview">
        <a-row :gutter="16">
          <a-col :span="6">
            <a-statistic 
              title="总执行次数" 
              :value="overview.total_executions"
              prefix-icon="PlayCircleOutlined"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="成功次数" 
              :value="overview.successful_executions"
              :value-style="{ color: '#3f8600' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="通过率" 
              :value="overview.pass_rate"
              suffix="%"
              :precision="1"
              :value-style="{ color: overview.pass_rate >= 80 ? '#3f8600' : '#cf1322' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="平均分数" 
              :value="overview.avg_score"
              :precision="2"
              suffix="分"
            />
          </a-col>
        </a-row>
      </a-card>

      <!-- 趋势图表 -->
      <a-row :gutter="16" style="margin-top: 16px">
        <a-col :span="12">
          <a-card title="通过率趋势">
            <div ref="passRateTrendChart" style="height: 300px"></div>
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card title="评分分布">
            <div ref="scoreDistributionChart" style="height: 300px"></div>
          </a-card>
        </a-col>
      </a-row>

      <!-- 用例性能分析 -->
      <a-card title="用例性能分析" style="margin-top: 16px" v-if="casePerformance.length > 0">
        <a-table
          :columns="performanceColumns"
          :data-source="casePerformance"
          :pagination="false"
          row-key="test_case_id"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'pass_rate'">
              <a-progress
                :percent="record.pass_rate"
                :status="record.pass_rate >= 80 ? 'success' : 'exception'"
                size="small"
              />
            </template>
            <template v-else-if="column.key === 'avg_score'">
              <a-tag :color="getScoreColor(record.avg_score)">
                {{ record.avg_score.toFixed(2) }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'trend'">
              <span :style="{ color: record.trend > 0 ? '#3f8600' : '#cf1322' }">
                {{ record.trend > 0 ? '↑' : '↓' }} {{ Math.abs(record.trend).toFixed(1) }}%
              </span>
            </template>
          </template>
        </a-table>
      </a-card>

      <!-- 评分分布统计 -->
      <a-row :gutter="16" style="margin-top: 16px">
        <a-col :span="12">
          <a-card title="评分等级分布">
            <div ref="scoreLevelChart" style="height: 300px"></div>
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card title="执行状态分布">
            <div ref="statusChart" style="height: 300px"></div>
          </a-card>
        </a-col>
      </a-row>

      <!-- Token消耗分析 -->
      <a-card title="Token消耗分析" style="margin-top: 16px" v-if="tokenStats">
        <a-row :gutter="16">
          <a-col :span="8">
            <a-statistic 
              title="总输入Token" 
              :value="tokenStats.total_input_tokens"
              suffix="tokens"
            />
          </a-col>
          <a-col :span="8">
            <a-statistic 
              title="总输出Token" 
              :value="tokenStats.total_output_tokens"
              suffix="tokens"
            />
          </a-col>
          <a-col :span="8">
            <a-statistic 
              title="平均每次Token" 
              :value="tokenStats.avg_tokens_per_execution"
              :precision="0"
              suffix="tokens"
            />
          </a-col>
        </a-row>
        <div ref="tokenTrendChart" style="height: 250px; margin-top: 24px"></div>
      </a-card>

      <!-- 快照对比分析 -->
      <a-card title="快照对比分析" style="margin-top: 16px" v-if="snapshotComparison.length > 0">
        <a-table
          :columns="snapshotColumns"
          :data-source="snapshotComparison"
          :pagination="false"
          row-key="snapshot_id"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'pass_rate'">
              <a-progress
                :percent="record.pass_rate"
                :status="record.pass_rate >= 80 ? 'success' : 'exception'"
                size="small"
              />
            </template>
            <template v-else-if="column.key === 'performance'">
              <a-tag :color="record.performance === 'improved' ? 'success' : record.performance === 'declined' ? 'error' : 'default'">
                {{ getPerformanceText(record.performance) }}
              </a-tag>
            </template>
          </template>
        </a-table>
      </a-card>
    </a-spin>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { message } from 'ant-design-vue';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { 
  getAnalysisOverview,
  getCasePerformance,
  getSnapshotComparison
} from '@/api/evalAnalysisV2';
import { getTestPlans } from '@/api/evalTestPlanV2';
import { getTestCases } from '@/api/evalTestCaseV2';

const loading = ref(false);
const dateRange = ref(null);
const testPlans = ref([]);
const testCases = ref([]);

const filters = reactive({
  test_plan_id: null,
  test_case_id: null
});

const overview = ref(null);
const casePerformance = ref([]);
const snapshotComparison = ref([]);
const tokenStats = ref(null);

// 图表引用
const passRateTrendChart = ref(null);
const scoreDistributionChart = ref(null);
const scoreLevelChart = ref(null);
const statusChart = ref(null);
const tokenTrendChart = ref(null);

const performanceColumns = [
  { title: '测试用例', dataIndex: 'case_name', key: 'case_name', width: 200 },
  { title: '执行次数', dataIndex: 'execution_count', key: 'execution_count', width: 100 },
  { title: '通过率', dataIndex: 'pass_rate', key: 'pass_rate', width: 150 },
  { title: '平均分数', dataIndex: 'avg_score', key: 'avg_score', width: 120 },
  { title: '平均耗时(ms)', dataIndex: 'avg_duration', key: 'avg_duration', width: 120 },
  { title: '趋势', key: 'trend', width: 100 }
];

const snapshotColumns = [
  { title: '快照名称', dataIndex: 'snapshot_name', key: 'snapshot_name', width: 200 },
  { title: '执行次数', dataIndex: 'execution_count', key: 'execution_count', width: 100 },
  { title: '通过率', dataIndex: 'pass_rate', key: 'pass_rate', width: 150 },
  { title: '平均分数', dataIndex: 'avg_score', key: 'avg_score', width: 120 },
  { title: '性能表现', key: 'performance', width: 120 }
];

const fetchData = async () => {
  loading.value = true;
  try {
    // 获取概览数据
    const overviewRes = await getAnalysisOverview(filters);
    if (overviewRes.code === 200) {
      overview.value = overviewRes.data?.overview;
      tokenStats.value = overviewRes.data?.token_stats;
      
      // 绘制图表
      renderCharts(overviewRes.data);
    }

    // 获取用例性能（仅当选择了测试用例时）
    if (filters.test_case_id) {
      const performanceRes = await getCasePerformance(filters.test_case_id);
      if (performanceRes.code === 200) {
        casePerformance.value = performanceRes.data || [];
      }
    }
  } catch (error) {
    message.error('获取分析数据失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const fetchTestPlans = async () => {
  try {
    const res = await getTestPlans({ pageSize: 100 });
    if (res.code === 200) {
      testPlans.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取测试计划失败:', error);
  }
};

const fetchTestCases = async () => {
  try {
    const res = await getTestCases({ status: 'active', pageSize: 100 });
    if (res.code === 200) {
      testCases.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取测试用例失败:', error);
  }
};

const renderCharts = (data) => {
  // 注意：这里使用简单的文本提示，实际项目中应该集成 ECharts 或其他图表库
  // 由于不确定项目中是否已经有图表库，这里只提供占位符
  console.log('图表数据:', data);
  
  // 如果有 ECharts，可以这样使用：
  // import * as echarts from 'echarts';
  // const chart = echarts.init(passRateTrendChart.value);
  // chart.setOption({...});
};

const exportReport = () => {
  message.info('导出报告功能开发中...');
  // 实际实现：将数据导出为 Excel 或 PDF
};

const getScoreColor = (score) => {
  if (score >= 90) return 'success';
  if (score >= 70) return 'processing';
  return 'warning';
};

const getPerformanceText = (performance) => {
  const texts = {
    improved: '提升',
    declined: '下降',
    stable: '稳定'
  };
  return texts[performance] || '-';
};

onMounted(() => {
  fetchTestPlans();
  fetchTestCases();
  fetchData();
});
</script>

<style scoped>
.analysis-report {
  padding: 24px;
}

:deep(.ant-statistic-title) {
  font-size: 14px;
  color: #666;
}

:deep(.ant-statistic-content) {
  font-size: 24px;
  font-weight: 500;
}
</style>

